import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Calendar,
  Vote,
  Award,
  Activity,
  ClipboardCheck,
  Plane,
  Settings,
  CalendarClock,
  Bot,
  Map,
  LogOut,
  Cpu,
  Siren,
} from 'lucide-react';
import apiService from '../services/api';

const navItems = [
  { path: '/', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/assistant', label: 'AI Assistant', icon: Bot },
  { path: '/personnel', label: 'Personnel', icon: Users },
  { path: '/roster', label: 'Roster', icon: Calendar },
  { path: '/optimizer', label: 'Optimizer', icon: Cpu },
  { path: '/irops', label: 'Disruption Desk', icon: Siren },
  { path: '/schedules', label: 'Schedules', icon: CalendarClock },
  { path: '/map', label: 'Route Map', icon: Map },
  { path: '/bidding', label: 'Bidding', icon: Vote },
  { path: '/qualifications', label: 'Qualifications', icon: Award },
  { path: '/frms', label: 'FRMS', icon: Activity },
  { path: '/audit', label: 'Audit', icon: ClipboardCheck },
  { path: '/settings', label: 'Settings', icon: Settings },
];

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onToggle: _onToggle }) => {
  const user = apiService.getCurrentUser();
  const handleLogout = () => {
    apiService.logout();
    window.location.href = '/login';
  };
  return (
    <aside className={`fixed left-0 top-0 z-50 h-screen w-64 border-r border-gray-800 dark:border-gray-800 bg-white dark:bg-gray-950/80 backdrop-blur-xl transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
      {/* Logo */}
      <div className="flex h-16 items-center gap-3 border-b border-gray-200 dark:border-gray-800 px-6">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-sky-500 to-sky-600">
          <Plane className="h-6 w-6 text-white" />
        </div>
        <div>
          <h1 className="text-lg font-bold text-gray-900 dark:text-white font-space-grotesk">CREW</h1>
          <p className="text-xs text-gray-500 dark:text-gray-400">FlightAtom</p>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 p-4">
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path === '/'}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-all ${
                isActive
                  ? 'bg-sky-500/10 text-sky-500 dark:text-sky-400 shadow-lg shadow-sky-500/20'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800/50 hover:text-gray-900 dark:hover:text-white'
              }`
            }
          >
            {({ isActive }) => (
              <>
                <item.icon className={`h-5 w-5 ${isActive ? 'text-sky-400' : ''}`} />
                <span>{item.label}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Footer */}
      <div className="border-t border-gray-200 dark:border-gray-800 p-4 space-y-3">
        {user && (
          <div className="rounded-lg bg-gray-100 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-800 px-3 py-2">
            <p className="text-xs text-gray-500 dark:text-gray-400">Signed in as</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
              {user.first_name} {user.last_name}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{user.employee_id}</p>
          </div>
        )}
        <button
          onClick={handleLogout}
          className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800/50 hover:text-gray-900 dark:hover:text-white"
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
